import React from 'react';
import { Star, Quote, MapPin, Briefcase, ChevronDown, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { getMediaUrl } from '../utils/mediaUrl';
import OptimizedImage from './OptimizedImage';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const CaseCard = ({ caseData, showDetails = true }) => {
  const {
    title,
    slug,
    customer_name,
    industry,
    location,
    service_used,
    quote,
    author_name,
    author_title,
    rating,
    featured_image,
    image_before,
    image_after,
    challenge,
    solution,
    results
  } = caseData;

  // Render stars
  const renderStars = (count) => {
    return Array.from({ length: 5 }, (_, i) => (
      <Star
        key={i}
        className={`w-4 h-4 ${i < count ? 'text-yellow-400 fill-yellow-400' : 'text-gray-600'}`}
      />
    ));
  };

  return (
    <article 
      className="group bg-gradient-to-br from-gray-800/60 to-gray-900/60 border border-gray-700 rounded-2xl overflow-hidden hover:border-red-600/40 transition-all duration-300"
      itemScope
      itemType="https://schema.org/Review"
    >
      {/* Images Section */}
      {(featured_image || image_before || image_after) && (
        <div className="relative h-48 overflow-hidden">
          {featured_image && typeof featured_image === 'string' ? (
            <OptimizedImage
              src={featured_image}
              alt={`Kundecase: ${customer_name}`}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            />
          ) : (image_before && typeof image_before === 'string' && image_after && typeof image_after === 'string') ? (
            <div className="flex h-full">
              <div className="w-1/2 relative">
                <OptimizedImage
                  src={image_before}
                  alt={`${customer_name} - Før`}
                  className="w-full h-full object-cover"
                />
                <span className="absolute bottom-2 left-2 px-2 py-1 bg-black/70 text-white text-xs rounded">Før</span>
              </div>
              <div className="w-1/2 relative">
                <OptimizedImage
                  src={image_after}
                  alt={`${customer_name} - Etter`}
                  className="w-full h-full object-cover"
                />
                <span className="absolute bottom-2 right-2 px-2 py-1 bg-green-600/90 text-white text-xs rounded">Etter</span>
              </div>
            </div>
          ) : null}
          <div className="absolute inset-0 bg-gradient-to-t from-gray-900 via-transparent to-transparent opacity-80"></div>
        </div>
      )}

      <div className="p-6">
        {/* Meta tags - First-View Content */}
        <div className="flex flex-wrap gap-2 mb-4">
          {industry && (
            <span className="inline-flex items-center px-2 py-1 text-xs bg-blue-600/20 text-blue-400 rounded-full">
              <Briefcase className="w-3 h-3 mr-1" />
              {industry}
            </span>
          )}
          {location && (
            <span className="inline-flex items-center px-2 py-1 text-xs bg-gray-700 text-gray-300 rounded-full">
              <MapPin className="w-3 h-3 mr-1" />
              {location}
            </span>
          )}
          {service_used && (
            <span className="inline-flex items-center px-2 py-1 text-xs bg-red-600/20 text-red-400 rounded-full">
              {service_used}
            </span>
          )}
        </div>

        {/* Title - First-View Content */}
        <h3 
          className="text-xl font-bold text-white mb-3 group-hover:text-red-400 transition-colors"
          itemProp="name"
        >
          {title}
        </h3>

        {/* Customer name - visible for SEO */}
        <p className="text-sm text-gray-400 mb-4" itemProp="author" itemScope itemType="https://schema.org/Organization">
          <span itemProp="name">{customer_name}</span>
        </p>

        {/* The Socratic Quote - First-View Content */}
        <blockquote className="relative mb-4" itemProp="reviewBody">
          <Quote className="absolute -top-2 -left-2 w-8 h-8 text-red-600/20" />
          <p className="text-gray-300 italic pl-6 leading-relaxed">
            "{quote}"
          </p>
          <footer className="mt-3 pl-6">
            <div className="flex items-center gap-3">
              <div className="flex">{renderStars(rating)}</div>
              <div className="text-sm">
                <span className="text-white font-medium">{author_name}</span>
                {author_title && (
                  <span className="text-gray-500">, {author_title}</span>
                )}
              </div>
            </div>
          </footer>
        </blockquote>

        {/* Rating for Schema */}
        <meta itemProp="reviewRating" content={rating} />

        {/* Expandable Details - Indexable but collapsed */}
        {showDetails && (challenge || solution || (results && results.length > 0)) && (
          <details className="mt-4 border-t border-gray-700 pt-4 group/details">
            <summary className="flex items-center justify-between cursor-pointer text-sm text-gray-400 hover:text-white transition-colors list-none">
              <span className="flex items-center gap-2">
                <ChevronDown className="w-4 h-4 transition-transform group-open/details:rotate-180" />
                Les hele casen
              </span>
            </summary>
            
            <div className="mt-4 space-y-4 text-sm">
              {/* Challenge - In DOM for crawlers */}
              {challenge && (
                <div>
                  <h4 className="text-red-400 font-semibold mb-2">Utfordringen</h4>
                  <p className="text-gray-400">{challenge}</p>
                </div>
              )}
              
              {/* Solution - In DOM for crawlers */}
              {solution && (
                <div>
                  <h4 className="text-yellow-400 font-semibold mb-2">Løsningen</h4>
                  <p className="text-gray-400">{solution}</p>
                </div>
              )}
              
              {/* Results - In DOM for crawlers */}
              {results && results.length > 0 && (
                <div>
                  <h4 className="text-green-400 font-semibold mb-2">Resultater</h4>
                  <ul className="space-y-1">
                    {results.map((result, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-gray-400">
                        <span className="text-green-500 mt-1">✓</span>
                        {result}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </details>
        )}

        {/* CTA */}
        {slug && (
          <Link
            to={`/referanse/${slug}`}
            className="inline-flex items-center mt-4 text-red-400 hover:text-red-300 text-sm font-medium transition-colors"
          >
            Les fullstendig case
            <ArrowRight className="w-4 h-4 ml-1" />
          </Link>
        )}
      </div>
    </article>
  );
};

export default CaseCard;
